import * as React from "react";
import { Check } from "lucide-react";
import { Button, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { STYLE_GROUPS, StyleChip } from "../styleChips";

interface StyleChipsProps {
  chips: StyleChip[];
  selectedIds: string[];
  onToggle(chip: StyleChip): void;
  disabled?: boolean;
}

// Long enough that sweeping across the chips doesn't flash every fragment.
const TOOLTIP_DELAY_MS = 600;

export const StyleChips: React.FC<StyleChipsProps> = ({ chips, selectedIds, onToggle, disabled }) => (
  <Tooltip.Provider delay={TOOLTIP_DELAY_MS}>
    <div className="flex flex-col gap-4">
      {STYLE_GROUPS.map(group => {
        const groupChips = chips.filter(chip => chip.group === group.id);
        if (groupChips.length === 0) return null;
        return (
          <div key={group.id} className="flex flex-col gap-2" data-testid={`chip-group-${group.id}`}>
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{group.label}</span>
            <div className="flex flex-wrap gap-2">
              {groupChips.map(chip => {
                const selected = selectedIds.includes(chip.id);
                return (
                  <Tooltip key={chip.id}>
                    <Tooltip.Trigger
                      render={
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          aria-pressed={selected}
                          disabled={disabled}
                          onClick={() => onToggle(chip)}
                          className={cn("rounded-full", selected && "bg-primary/10 border-primary text-primary")}
                        />
                      }
                    >
                      {selected && <Check aria-hidden="true" />}
                      {chip.label}
                    </Tooltip.Trigger>
                    <Tooltip.Content>{chip.fragment}</Tooltip.Content>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  </Tooltip.Provider>
);
